import React, { useMemo } from 'react';
import AdminToggle from '../admin/AdminToggle';
import {
  DEFAULT_BRACELET_SIZES,
  DEFAULT_CHAIN_OPTIONS,
  DEFAULT_RING_SIZES,
  getEnabledFieldIds,
  getProfileFieldDefs,
  normalizeProductJewelryOptions,
} from './jewelryCustomization';
import { getJewelryProfile } from './jewelryProfileRegistry';
import JewelryOptionListEditor from './JewelryOptionListEditor';

function optionsKeyForField(field) {
  return field.optionsKey || '';
}

function defaultOptionsForKey(key) {
  if (key === 'chain') return DEFAULT_CHAIN_OPTIONS;
  if (key === 'ring') return DEFAULT_RING_SIZES;
  if (key === 'bracelet') return DEFAULT_BRACELET_SIZES;
  return [];
}

function optionsKeyToFormKey(key) {
  if (key === 'chain') return 'chainOptions';
  if (key === 'ring') return 'ringOptions';
  if (key === 'bracelet') return 'braceletOptions';
  return '';
}

/**
 * Per-product jewelry options — which fields show, required overrides, chain/ring sizes.
 */
export default function JewelryProfileAdminOptions({ profileId, options, onChange }) {
  const profile = profileId ? getJewelryProfile(profileId) : null;
  const productLike = useMemo(
    () => ({
      customization_profile: profileId,
      customization_options: normalizeProductJewelryOptions({
        customization_profile: profileId,
        customization_options: options,
      }),
    }),
    [profileId, options]
  );

  if (!profile) return null;

  const allFields = getProfileFieldDefs(profileId);
  const optionalFields = allFields.filter((f) => f.optional);
  const normalizedOptions = productLike.customization_options;
  const enabledIds = getEnabledFieldIds(productLike);
  const requiredFields = normalizedOptions.requiredFields || {};

  const patchOptions = (patch) => {
    onChange?.(
      normalizeProductJewelryOptions({
        customization_profile: profileId,
        customization_options: { ...normalizedOptions, ...patch, configVersion: 2 },
      })
    );
  };

  const toggleField = (fieldId, on) => {
    const coreIds = allFields.filter((f) => !f.optional).map((f) => f.id);
    const normalized = normalizeProductJewelryOptions({
      customization_profile: profileId,
      customization_options: options,
    });
    let next = [...getEnabledFieldIds({ customization_profile: profileId, customization_options: normalized })];
    if (on && !next.includes(fieldId)) next.push(fieldId);
    if (!on) next = next.filter((id) => id !== fieldId);
    for (const id of coreIds) {
      if (!next.includes(id)) next.unshift(id);
    }
    const nextRequired = { ...(normalized.requiredFields || {}) };
    if (!on) delete nextRequired[fieldId];
    onChange?.({
      ...normalized,
      configVersion: 2,
      enabledFields: next,
      requiredFields: nextRequired,
    });
  };

  const toggleRequired = (fieldId, on) => {
    const normalized = normalizeProductJewelryOptions({
      customization_profile: profileId,
      customization_options: options,
    });
    onChange?.({
      ...normalized,
      configVersion: 2,
      requiredFields: { ...(normalized.requiredFields || {}), [fieldId]: on },
    });
  };

  const selectFieldsWithOptions = allFields.filter(
    (f) => f.type === 'select' && enabledIds.includes(f.id) && f.optionsKey
  );

  return (
    <div className="jewelry-profile-admin mt-3">
      <p className="small fw-semibold mb-2">What customers see for this product</p>
      <ul className="list-unstyled mb-0 jewelry-profile-admin__core">
        {allFields
          .filter((f) => !f.optional)
          .map((f) => (
            <li key={f.id} className="small text-muted mb-1">
              ✓ {f.label} {f.required ? '(required)' : ''}
            </li>
          ))}
      </ul>

      {optionalFields.length > 0 ? (
        <div className="jewelry-profile-admin__optional mt-3">
          <p className="small text-muted mb-2">Optional — turn on only if this product needs them:</p>
          {optionalFields.map((field) => {
            const on = enabledIds.includes(field.id);
            return (
              <div key={field.id} className="jewelry-profile-admin__row mb-2">
                <AdminToggle
                  id={`jewelry-field-${profileId}-${field.id}`}
                  label={field.adminLabel || field.label}
                  checked={on}
                  onChange={(v) => toggleField(field.id, v)}
                />
                {on && field.type === 'select' ? (
                  <div className="ms-4 mt-1">
                    <AdminToggle
                      id={`jewelry-req-${profileId}-${field.id}`}
                      label={`Customer must choose ${field.label.toLowerCase()}`}
                      checked={Boolean(requiredFields[field.id])}
                      onChange={(v) => toggleRequired(field.id, v)}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {selectFieldsWithOptions.map((field) => {
        const key = optionsKeyForField(field);
        const formKey = optionsKeyToFormKey(key);
        if (!formKey) return null;
        const custom = normalizedOptions[formKey];
        const usingCustom = Array.isArray(custom) && custom.length > 0;
        const defaults = defaultOptionsForKey(key);
        return (
          <div key={`opts-${field.id}`} className="jewelry-profile-admin__sizes mt-3">
            <AdminToggle
              id={`jewelry-custom-${profileId}-${field.id}`}
              label={`Custom ${field.label.toLowerCase()} choices (instead of store defaults)`}
              checked={usingCustom}
              onChange={(v) => {
                if (!v) {
                  const next = { ...normalizedOptions };
                  delete next[formKey];
                  onChange?.(
                    normalizeProductJewelryOptions({
                      customization_profile: profileId,
                      customization_options: next,
                    })
                  );
                  return;
                }
                patchOptions({ [formKey]: defaults });
              }}
            />
            {usingCustom ? (
              <div className="mt-2">
                <JewelryOptionListEditor
                  options={custom}
                  showPrice={key === 'chain'}
                  defaultOptions={defaults}
                  onChange={(next) => patchOptions({ [formKey]: next })}
                />
              </div>
            ) : (
              <p className="small text-muted mb-0 mt-2">
                Defaults: {defaults.map((o) => o.label).join(', ')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
